'use client';

import { CheckCircleIcon, XCircleIcon } from '@heroicons/react/24/outline';
import Link from 'next/link';
import { trackButtonClick } from '@/lib/analytics';
import { PUBLIC_SALE_STARTING_PRICE } from '@/types/pricing';

const rows = [
  { feature: 'Typical monthly cost', answering: '$200–$400+', greet: `From ${PUBLIC_SALE_STARTING_PRICE.replace('/month', '')}` },
  { feature: 'Pricing model', answering: 'Per-minute / per-call + overages', greet: 'Flat monthly rate' },
  { feature: 'After-hours / weekends', answering: 'Often surcharged', greet: 'Included 24/7' },
  { feature: 'Books appointments', answering: 'Rare on base plans', greet: 'Yes, into your calendar' },
  { feature: 'Knows your FAQs & pricing', answering: 'Shallow scripts', greet: 'Trained on your business' },
  { feature: 'Busy-month bill surprises', answering: 'Common', greet: 'None' },
];

type Props = {
  industryLabel: string;
  analyticsContext: string;
  accent?: 'blue' | 'emerald';
};

export default function AnsweringServiceVsGreet({
  industryLabel,
  analyticsContext,
  accent = 'blue',
}: Props) {
  const isEmerald = accent === 'emerald';
  const headerBg = isEmerald ? 'bg-emerald-600' : 'bg-blue-600';
  const greetCol = isEmerald ? 'bg-emerald-50 text-emerald-900' : 'bg-blue-50 text-blue-900';
  const ctaBg = isEmerald
    ? 'bg-emerald-600 hover:bg-emerald-700'
    : 'bg-blue-600 hover:bg-blue-700';

  return (
    <section className="py-20 bg-white">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Answering Service vs AI for {industryLabel}
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Traditional answering services take messages. Greet answers, books, and covers nights
            without per-minute fees.
          </p>
        </div>

        <div className="overflow-x-auto rounded-2xl border border-gray-200 shadow-sm">
          <table className="min-w-full text-sm">
            <thead>
              <tr>
                <th className="px-4 py-4 text-left font-semibold text-gray-900 bg-gray-50 border-b">
                  Feature
                </th>
                <th className="px-4 py-4 text-left font-semibold text-gray-700 bg-gray-100 border-b">
                  Typical answering service
                </th>
                <th className={`px-4 py-4 text-left font-semibold text-white border-b ${headerBg}`}>
                  Greet AI receptionist
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => (
                <tr key={row.feature} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                  <td className="px-4 py-3 font-medium text-gray-900 border-b border-gray-100">
                    {row.feature}
                  </td>
                  <td className="px-4 py-3 text-gray-600 border-b border-gray-100">
                    <span className="inline-flex items-start gap-2">
                      <XCircleIcon className="h-5 w-5 text-red-400 shrink-0 mt-0.5" />
                      {row.answering}
                    </span>
                  </td>
                  <td className={`px-4 py-3 font-medium border-b border-gray-100 ${greetCol}`}>
                    <span className="inline-flex items-start gap-2">
                      <CheckCircleIcon
                        className={`h-5 w-5 shrink-0 mt-0.5 ${isEmerald ? 'text-emerald-600' : 'text-blue-600'}`}
                      />
                      {row.greet}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center">
          <Link
            href="/try"
            onClick={() => trackButtonClick('try_demo', analyticsContext)}
            className={`${ctaBg} text-white px-8 py-4 rounded-lg font-semibold text-lg transition-colors text-center`}
          >
            Hear it answer in 60 seconds
          </Link>
          <Link
            href="/pricing"
            onClick={() => trackButtonClick('view_pricing', analyticsContext)}
            className="border border-gray-300 text-gray-800 hover:bg-gray-50 px-8 py-4 rounded-lg font-semibold text-lg transition-colors text-center"
          >
            See full pricing
          </Link>
        </div>
      </div>
    </section>
  );
}
